import * as React from "react";

import { getPlanName } from "../../../areas/pricePlan/getPlanName";
import { useLazyApi } from "../../../hooks/useApi/useApi";
import * as proxy from "../../../services/api/proxy";
import * as m from "../../model/model";
import { Day } from "../../model/model";
import { useCompanyInfo } from "../CompanyInfoContext/CompanyInfoContext";
import { useFeatureAvailability } from "../FeatureAvailabilityContext/FeatureAvailabilityContext";
import { PricePlanContext } from "./PricePlanContext";

import BokioPlan = m.Entities.BokioPlan;

export const PricePlanContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const { companyInfo } = useCompanyInfo();
	// We need a null check here because the company info is not always available when this component is mounted
	// even if the companyInfo "can't" be undefined if you look at the type.
	const companyId = companyInfo?.Id;
	const [getPlan, currentPlanRequest] = useLazyApi(
		proxy.Bokio.Common.Web.Settings.SubscriptionController.CurrentBokioPlan.Get,
	);

	React.useEffect(() => {
		if (companyId) {
			getPlan(companyId);
		}
	}, [companyId, getPlan]);

	const planInfo = currentPlanRequest.data?.Data?.PlanInfo;
	// Last day (end of trial info) should be take as 1 day left
	const dayDiffUntilPlanChange = (currentPlanRequest.data?.Data?.PlannedChange?.Date.dayDiff(Day.today()) ?? -1) + 1;
	const daysUntilTrialEnded = planInfo?.IsTrialing && dayDiffUntilPlanChange > 0 ? dayDiffUntilPlanChange : undefined;

	const features = useFeatureAvailability();

	// This part should probably be moved to the backend so it returns the higest plan possible for the country
	const isPlusAvailable = (features.Country as m.CountryCode) !== m.CountryCode.GB;
	const highestPlans = isPlusAvailable
		? [BokioPlan.Plus_2025, BokioPlan.Pro]
		: [BokioPlan.Premium];

	const canPurchase = !highestPlans.includes(planInfo?.Plan as BokioPlan);

	return (
		<PricePlanContext
			value={{
				plan: currentPlanRequest.isLoading ? undefined : planInfo?.Plan,
				planInfo: currentPlanRequest.isLoading ? undefined : planInfo,
				inTrial: planInfo?.IsTrialing ?? false,
				daysUntilTrialEnded: currentPlanRequest.isLoading ? undefined : daysUntilTrialEnded,
				planName: planInfo?.Plan ? getPlanName(planInfo?.Plan) : "",
				plannedChange: currentPlanRequest.data?.Data?.PlannedChange?.Date,
				nextPlan: currentPlanRequest.data?.Data?.PlannedChange?.Plan,
				nextPlanName: currentPlanRequest.data?.Data?.PlannedChange?.Plan
					? getPlanName(currentPlanRequest.data?.Data?.PlannedChange?.Plan)
					: undefined,
				hasPurchasedPlan:
					(!planInfo?.IsTrialing && planInfo?.Plan !== BokioPlan.Free) ||
					(planInfo?.IsTrialing && currentPlanRequest.data?.Data?.PlannedChange?.Plan !== BokioPlan.Free),
				canPurchase,
				request: currentPlanRequest,
				refresh: async () => {
					if (companyId) {
						getPlan(companyId);
					}
				},
			}}
		>
			{children}
		</PricePlanContext>
	);
};
